import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useExpress, { expressMutation } from '../../repository/react-query/useExpress';
import { getId, getRole } from '../../../utils';
import { buildExpressQuery } from '../../repository/queries/builders';
import axios from 'axios';
import { restoreCache, setDeletedQueryCache, updateQueryCache } from '../optimistic-update-utils';

const baseEndpoint = '/scale-readings';
const baseQueryKey = 'scale-reading-data';

const buildQueryParams = ({ queryParams, includeDeleted, restrictByRole = true }) => {
    const { where = {}, ...rest } = queryParams || {};
    if (!includeDeleted) {
        where.deleted_at = null;
    }
    if (restrictByRole) {
        const role = getRole();
        if (role === 'transport') {
            where.hauling_id = getId();
        }
        if (role === 'processor') {
            where.processor_id = getId();
        }
    }

    return {
        where,
        ...rest,
    };
};

export const getScaleReadingsQueryFn = ({ path = '', includeDeleted = false, queryParams, restrictByRole } = {}) => {
    const endpoint = `${baseEndpoint}${path}`;
    const params = buildQueryParams({ queryParams, includeDeleted, restrictByRole });
    return useExpress({ endpoint, params });
};

export const useFetchScaleReadings = ({ enabled = true, key = baseQueryKey, includeDeleted = false, queryParams, useQueryOptions, restrictByRole } = {}) => {
    const queryFn = getScaleReadingsQueryFn({ path: '/list', includeDeleted, queryParams, restrictByRole });
    const queryKey = [key, { includeDeleted, queryParams }];
    return useQuery({ queryKey, queryFn, enabled, ...useQueryOptions });
};

// pure getter for use outside react-query
export const getScaleReadings = async ({ includeDeleted = false, queryParams, restrictByRole, path = '' } = {}) => {
    const endpoint = `${baseEndpoint}${path}`;
    const params = buildQueryParams({ queryParams, includeDeleted, restrictByRole });
    const query = buildExpressQuery(endpoint, { params });
    try {
        const response = await axios.get(query);
        return {
            success: true,
            data: response.data,
        };
    } catch (error) {
        return {
            success: false,
            error: error.message,
        };
    }
};

export const useCreateScaleReading = ({ key = baseQueryKey }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ data }) => {
            return expressMutation({
                method: 'POST',
                endpoint: `${baseEndpoint}`,
                body: data,
            });
        },
        onMutate: (updatedItem) => updateQueryCache(queryClient, [key], { id: updatedItem.id, ...updatedItem.data }),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useUpdateScaleReading = ({ key = baseQueryKey }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, data }) => {
            return expressMutation({
                method: 'PATCH',
                endpoint: `${baseEndpoint}/${id}`,
                body: data,
            });
        },
        onMutate: (updatedItem) => updateQueryCache(queryClient, [key], { id: updatedItem.id, ...updatedItem.data }),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};

export const useDeleteScaleReading = ({ key = baseQueryKey }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id }) => {
            return expressMutation({
                method: 'DELETE',
                endpoint: `${baseEndpoint}/${id}`,
            });
        },
        onMutate: (updatedItem) => setDeletedQueryCache(queryClient, [key], updatedItem),
        onError: (_err, _, ctx) => restoreCache(queryClient, ctx),
        onSuccess: () => {
            if (key) {
                queryClient.invalidateQueries({ queryKey: [key] });
            }
        },
    });
};
